/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { addDays } from 'date-fns';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Suggestions, RoomConfig } from '../types';
import {
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_RETURN_OFFSET_DAYS,
  HOTEL,
} from '../configs/constants';
import { useIsClientHydrated } from './useIsClientHydrated';

export type HotelSearchFormState = {
  destination: Suggestions | null;
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  rooms: RoomConfig[];
};

export type HotelSearchFormInitialValues = {
  destination: Suggestions | null;
  checkIn: Date;
  checkOut: Date;
  rooms: RoomConfig[];
  source?: 'default' | 'recent-search' | 'deeplink';
};

export const HOTEL_SEARCH_FORM_STATE_KEY = [
  QUERY_PERSIST_CACHE,
  'hotels',
  'searchFormState',
] as const;

type PersistedHotelSearchFormState = Omit<
  HotelSearchFormState,
  'checkIn' | 'checkOut'
> & {
  checkIn?: Date | string;
  checkOut?: Date | string;
};

const toDate = (value: Date | string | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const getDefaultSearchFormState = (
  initialValues?: HotelSearchFormInitialValues,
): HotelSearchFormState => {
  const today = new Date();
  const initialCheckIn = toDate(initialValues?.checkIn);
  const initialCheckOut = toDate(initialValues?.checkOut);

  return {
    destination: initialValues?.destination || null,
    checkIn: initialCheckIn || addDays(today, DEFAULT_DEPARTURE_OFFSET_DAYS),
    checkOut: initialCheckOut || addDays(today, DEFAULT_RETURN_OFFSET_DAYS),
    rooms: initialValues?.rooms?.length
      ? initialValues.rooms
      : [{ id: '1', adults: 2, children: [] }],
  };
};

const getInitialValuesSignature = (
  initialValues?: HotelSearchFormInitialValues,
): string | null => {
  if (!initialValues) {
    return null;
  }

  return JSON.stringify({
    destinationId: initialValues.destination?.id ?? null,
    destinationAaId: initialValues.destination?.aa_id ?? null,
    destinationType: initialValues.destination?.type ?? null,
    checkIn: toDate(initialValues.checkIn)?.toISOString() ?? null,
    checkOut: toDate(initialValues.checkOut)?.toISOString() ?? null,
    rooms: initialValues.rooms,
  });
};

const getInitialDestinationDisplaySignature = (
  initialValues?: HotelSearchFormInitialValues,
): string | null => {
  const destination = initialValues?.destination;

  if (!destination) {
    return null;
  }

  return JSON.stringify({
    destinationId: destination.id ?? null,
    destinationAaId: destination.aa_id ?? null,
    destinationType: destination.type ?? null,
    destinationName: destination.name ?? null,
    destinationFullName: destination.full_name ?? null,
  });
};

const isHotelDestination = (destination: Suggestions | null | undefined) =>
  destination?.type?.toLowerCase() === HOTEL ||
  Boolean(destination?.aa_id?.trim());

const resolveDestination = (
  persistedDestination: Suggestions | null | undefined,
  fallbackDestination: Suggestions | null,
  preferFallbackDestination: boolean,
): Suggestions | null => {
  if (!persistedDestination) {
    return fallbackDestination;
  }

  if (!fallbackDestination) {
    return persistedDestination;
  }

  if (
    preferFallbackDestination &&
    fallbackDestination.id &&
    fallbackDestination.id !== persistedDestination.id
  ) {
    return fallbackDestination;
  }

  if (
    fallbackDestination.id &&
    fallbackDestination.id === persistedDestination.id
  ) {
    const isPersistedHotel = isHotelDestination(persistedDestination);
    const isFallbackHotel = isHotelDestination(fallbackDestination);
    const persistedHotelId = persistedDestination.aa_id?.trim() || '';
    const fallbackHotelId = fallbackDestination.aa_id?.trim() || '';
    const isSameDestinationSelection =
      isPersistedHotel === isFallbackHotel &&
      persistedHotelId === fallbackHotelId;

    // A location can contain multiple hotels or switch between a hotel and its
    // surrounding city. Keep the active selection based on whether we are
    // applying initialValues (preferFallbackDestination) or normalizing user
    // form updates (!preferFallbackDestination).
    if (!isSameDestinationSelection) {
      if (!preferFallbackDestination) {
        return {
          ...fallbackDestination,
          ...persistedDestination,
          aa_id: isPersistedHotel ? persistedHotelId : '',
          city_id: persistedDestination.city_id || fallbackDestination.city_id,
          country_code:
            persistedDestination.country_code ||
            fallbackDestination.country_code,
          coordinates:
            persistedDestination.coordinates || fallbackDestination.coordinates,
        };
      }

      return {
        ...persistedDestination,
        ...fallbackDestination,
        aa_id: isFallbackHotel ? fallbackHotelId : '',
        city_id: fallbackDestination.city_id || persistedDestination.city_id,
        country_code:
          fallbackDestination.country_code || persistedDestination.country_code,
        coordinates:
          fallbackDestination.coordinates || persistedDestination.coordinates,
      };
    }

    return {
      ...fallbackDestination,
      ...persistedDestination,
      // Preserve user-selected display label while enriching missing metadata from fallback.
      name: persistedDestination.name || fallbackDestination.name,
      full_name:
        persistedDestination.full_name || fallbackDestination.full_name,
      aa_id: persistedDestination.aa_id ?? fallbackDestination.aa_id,
      city_id: persistedDestination.city_id || fallbackDestination.city_id,
      country_code:
        persistedDestination.country_code || fallbackDestination.country_code,
      coordinates:
        persistedDestination.coordinates || fallbackDestination.coordinates,
      type: persistedDestination.type || fallbackDestination.type,
    };
  }

  return persistedDestination;
};

const normalizeFormState = (
  state: PersistedHotelSearchFormState | undefined,
  fallbackState: HotelSearchFormState,
  preferFallbackDestination = false,
): HotelSearchFormState => {
  if (!state) {
    return fallbackState;
  }

  const today = startOfDay(new Date());
  const parsedCheckIn = toDate(state.checkIn);
  const parsedCheckOut = toDate(state.checkOut);
  const isPast = (date: Date | undefined) => !!date && startOfDay(date) < today;

  return {
    destination: resolveDestination(
      state.destination,
      fallbackState.destination,
      preferFallbackDestination,
    ),
    checkIn: isPast(parsedCheckIn)
      ? fallbackState.checkIn
      : (parsedCheckIn ?? fallbackState.checkIn),
    checkOut: isPast(parsedCheckOut)
      ? fallbackState.checkOut
      : (parsedCheckOut ?? fallbackState.checkOut),
    rooms: state.rooms?.length ? state.rooms : fallbackState.rooms,
  };
};

const applyInitialValuesToState = (
  state: PersistedHotelSearchFormState | undefined,
  fallbackState: HotelSearchFormState,
  initialValues?: HotelSearchFormInitialValues,
): HotelSearchFormState => {
  if (!state) {
    return fallbackState;
  }

  if (initialValues?.source === 'recent-search') {
    const normalizedState = normalizeFormState(state, fallbackState);

    return {
      ...normalizedState,
      destination: fallbackState.destination,
    };
  }

  // Take the incoming criteria, but resolve the destination rather than
  // replacing it outright. A destination rebuilt from listing params carries
  // only the ids — its label comes from a smart-fill lookup on the location id,
  // so for a hotel it is either blank or the surrounding area. Resolving keeps
  // the label already on screen when the identity is unchanged, and still lets
  // a genuinely different destination take over.
  return {
    ...fallbackState,
    destination: resolveDestination(
      state.destination,
      fallbackState.destination,
      true,
    ),
  };
};

export const useHotelSearchFormState = (
  initialValues?: HotelSearchFormInitialValues,
) => {
  const queryClient = useQueryClient();
  const appliedInitialValuesSignatureRef = useRef<string | null>(null);
  const defaultFormState = useMemo(
    () => getDefaultSearchFormState(initialValues),
    [initialValues],
  );
  const initialValuesSignature = useMemo(
    () => getInitialValuesSignature(initialValues),
    [initialValues],
  );
  const initialDestinationDisplaySignature = useMemo(
    () => getInitialDestinationDisplaySignature(initialValues),
    [initialValues],
  );

  // Ensure query is initialized with staleTime: Infinity and gcTime: Infinity
  // This prevents the data from ever being garbage collected or cleared
  queryClient.setQueryDefaults(HOTEL_SEARCH_FORM_STATE_KEY, {
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      queryClient.getQueryCache().subscribe(() => {
        onStoreChange();
      }),
    [queryClient],
  );

  const getSnapshot = useCallback(
    () =>
      queryClient.getQueryData<PersistedHotelSearchFormState>(
        HOTEL_SEARCH_FORM_STATE_KEY,
      ),
    [queryClient],
  );

  const getServerSnapshot = useCallback(() => undefined, []);
  const isClientHydrated = useIsClientHydrated();

  const rawPersistedState = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  const persistedState = isClientHydrated ? rawPersistedState : undefined;

  useEffect(() => {
    if (
      !initialValues ||
      !initialValuesSignature ||
      appliedInitialValuesSignatureRef.current === initialValuesSignature
    ) {
      return;
    }

    appliedInitialValuesSignatureRef.current = initialValuesSignature;
    queryClient.setQueryData<HotelSearchFormState>(
      HOTEL_SEARCH_FORM_STATE_KEY,
      (oldState) =>
        applyInitialValuesToState(oldState, defaultFormState, initialValues),
    );
  }, [defaultFormState, initialValues, initialValuesSignature, queryClient]);

  useEffect(() => {
    const nextDestination = initialValues?.destination;

    if (!nextDestination || !initialDestinationDisplaySignature) {
      return;
    }

    queryClient.setQueryData<HotelSearchFormState>(
      HOTEL_SEARCH_FORM_STATE_KEY,
      (oldState) => {
        const normalizedState = normalizeFormState(oldState, defaultFormState);
        const currentDestination = normalizedState.destination;

        // Map→search (and other deferred seeds) can land after an empty first
        // paint. If the cache still has no destination, apply the incoming one
        // instead of no-oping and leaving the field blank.
        if (!currentDestination) {
          return {
            ...normalizedState,
            destination: nextDestination,
          };
        }

        const hasSameDestinationIdentity =
          currentDestination.id === nextDestination.id &&
          (currentDestination.aa_id || '') === (nextDestination.aa_id || '') &&
          (currentDestination.type || '') === (nextDestination.type || '');

        if (!hasSameDestinationIdentity) {
          return oldState;
        }

        // The product requirement is that when a user searches for a specific hotel,
        // the search bar should eventually update to show the destination (city) name
        // (e.g., "Cebu") rather than keeping the hotel name (e.g., "Savoy Hotel").
        // We prioritize the incoming name from initialValues (which is resolved to the city
        // by the parent components) over the currently cached name.
        const nextName = nextDestination.name || currentDestination.name;
        const nextFullName =
          nextDestination.full_name || currentDestination.full_name;

        if (
          currentDestination.name === nextName &&
          currentDestination.full_name === nextFullName
        ) {
          return oldState;
        }

        return {
          ...normalizedState,
          destination: {
            ...currentDestination,
            ...nextDestination,
            name: nextName,
            full_name: nextFullName,
          },
        };
      },
    );
  }, [
    defaultFormState,
    initialDestinationDisplaySignature,
    initialValues?.destination,
    queryClient,
  ]);

  const formState = useMemo(
    () =>
      initialValues &&
      initialValuesSignature &&
      appliedInitialValuesSignatureRef.current !== initialValuesSignature
        ? applyInitialValuesToState(
            persistedState,
            defaultFormState,
            initialValues,
          )
        : normalizeFormState(persistedState, defaultFormState),
    [persistedState, defaultFormState, initialValues, initialValuesSignature],
  );

  const updateFormState = useCallback(
    (updates: Partial<HotelSearchFormState>) => {
      queryClient.setQueryData<HotelSearchFormState>(
        HOTEL_SEARCH_FORM_STATE_KEY,
        (oldState) => {
          const normalizedState = normalizeFormState(
            oldState,
            defaultFormState,
          );

          const nextState = normalizeFormState(
            {
              ...normalizedState,
              ...updates,
            },
            defaultFormState,
          );

          return nextState;
        },
      );
    },
    [queryClient, defaultFormState],
  );

  const resetFormState = useCallback(() => {
    queryClient.setQueryData<HotelSearchFormState>(
      HOTEL_SEARCH_FORM_STATE_KEY,
      defaultFormState,
    );
  }, [queryClient, defaultFormState]);

  // Reads the query cache directly instead of the reactive `formState` above.
  // A sibling component's write (e.g. confirming a date in the calendar) is
  // synchronous in the cache, but this component's own re-render from that
  // write is not guaranteed to have landed by the time a user interaction
  // (like tapping a featured destination) fires right after. Consumers that
  // need the freshest value at interaction time should call this instead of
  // closing over `formState`.
  const getFormState = useCallback(
    () => normalizeFormState(getSnapshot(), defaultFormState),
    [getSnapshot, defaultFormState],
  );

  return {
    formState,
    updateFormState,
    resetFormState,
    getFormState,
  };
};
